import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread, GetAgentThreadsResponse } from '@dreamai/types';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import api from '../../../api';
import { ReactComponent as SendIcon } from '../../../icons/send.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as ClipboardIcon } from '../../../icons/clipboard-list.svg';
import { ReactComponent as MentionAtIcon } from '../../../icons/mention-at.svg';
import { ReactComponent as TasksIcon } from '../../../icons/clipboard-list.svg';
import './side-nav-section.scss';

export interface TasksSectionRef {
  refreshThreads: () => void;
}

const TasksSection = forwardRef<TasksSectionRef>((_, ref) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const { getAllUnreadThreads } = useThreadReads();

  const currentThreadId = searchParams.get('thread_id');

  // Get all unread thread IDs
  const unreadThreadIds = useMemo(
    () => new Set(getAllUnreadThreads().map((t) => t.thread_id)),
    [getAllUnreadThreads],
  );

  const [threads, setThreads] = useState<AgentThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const MAX_VISIBLE_THREADS = 10;

  const fetchThreads = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      // Fetch all threads without agent filter
      const response = await api.get<GetAgentThreadsResponse>(
        '/agents/threads',
        {
          params: {
            excludePinned: 'true',
            excludeShared: 'true',
            limit: 50,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setThreads(response.data.threads);
      }
    } catch (err) {
      console.error('Error fetching threads for tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshThreads: fetchThreads,
  }));

  useEffect(() => {
    fetchThreads();
  }, [getAccessTokenSilently]);

  // Filter threads - only show HumanTask threads for ops users
  const visibleThreads = useMemo(
    () =>
      threads.filter(
        (t) =>
          !t.is_archived_thread &&
          t.discussing_objects?.some(
            (obj) => obj.discussing_object === 'HumanTask',
          ),
      ),
    [threads],
  );

  // Sort by priority: mentions > confirmations > unread > recent
  const sortedThreads = useMemo(() => {
    return [...visibleThreads].sort((a, b) => {
      if (a.has_unread_mentions && !b.has_unread_mentions) return -1;
      if (!a.has_unread_mentions && b.has_unread_mentions) return 1;
      if (a.has_pending_confirmations && !b.has_pending_confirmations)
        return -1;
      if (!a.has_pending_confirmations && b.has_pending_confirmations) return 1;
      const aUnread = unreadThreadIds.has(a.id);
      const bUnread = unreadThreadIds.has(b.id);
      if (aUnread && !bUnread) return -1;
      if (!aUnread && bUnread) return 1;
      return (
        new Date(b.last_updated_at).getTime() -
        new Date(a.last_updated_at).getTime()
      );
    });
  }, [visibleThreads, unreadThreadIds]);

  const displayedThreads = isExpanded
    ? sortedThreads
    : sortedThreads.slice(0, MAX_VISIBLE_THREADS);
  const hasHiddenThreads =
    !isExpanded && sortedThreads.length > MAX_VISIBLE_THREADS;
  const hiddenCount = sortedThreads.length - MAX_VISIBLE_THREADS;

  const getThreadIcon = (thread: AgentThread) => {
    let icon = <SendIcon />;
    if (
      thread.discussing_objects?.some((d) => d.discussing_object === 'Campaign')
    ) {
      icon = <CampaignIcon />;
    } else if (
      thread.discussing_objects?.some((d) => d.discussing_object === 'Plan')
    ) {
      icon = <ClipboardIcon />;
    }

    if (thread.has_unread_mentions) return <MentionAtIcon />;

    if (thread.has_pending_confirmations) {
      return (
        <div className='thread-icon-wrapper'>
          {icon}
          <div className='action-required-indicator'>
            <div className='action-required-indicator-dot' />
          </div>
        </div>
      );
    }

    return icon;
  };

  const getThreadName = (thread: AgentThread) => {
    if (thread.is_main_thread) return 'Direct Message';
    return thread.description || 'Untitled';
  };

  const getThreadClasses = (thread: AgentThread) => {
    const classes = ['thread-item'];
    if (currentThreadId === thread.id) classes.push('active');
    if (unreadThreadIds.has(thread.id)) classes.push('has-unread');
    if (thread.has_pending_confirmations)
      classes.push('has-pending-confirmations');
    if (thread.has_unread_mentions) classes.push('has-unread-mentions');
    return classes.join(' ');
  };

  if (loading && threads.length === 0) {
    return null;
  }

  if (sortedThreads.length === 0) {
    return null;
  }

  return (
    <div className='side-nav-section tasks-section'>
      <div className='section-header'>
        <div className='agent-info'>
          <div className='dreamie-image-wrapper tasks-icon'>
            <TasksIcon />
          </div>
          <p className='nickname'>Tasks</p>
        </div>
      </div>

      <div className='threads'>
        {displayedThreads.map((thread) => (
          <Link
            key={thread.id}
            to={`/conversations?agent_id=${thread.agent_id}&thread_id=${thread.id}`}
            className={getThreadClasses(thread)}
          >
            {getThreadIcon(thread)}
            <span>{getThreadName(thread)}</span>
          </Link>
        ))}

        {hasHiddenThreads && (
          <button
            className='expand-threads-btn'
            onClick={() => setIsExpanded(true)}
          >
            + {hiddenCount} more tasks
          </button>
        )}

        {isExpanded && sortedThreads.length > MAX_VISIBLE_THREADS && (
          <button
            className='expand-threads-btn'
            onClick={() => setIsExpanded(false)}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
});

TasksSection.displayName = 'TasksSection';

export default TasksSection;
